<template>
    <BaseCard
        radius="lgr"
        padding="none"
        class="w-full min-w-0 overflow-hidden"
        :class="card?.status === 'complete' ? 'md:w-full max-w-[600px]' : 'max-w-[600px] bg-surface'"
        data-testid="collaboration-analysis-card">
        <div v-if="card?.status === 'complete'" class="flex flex-col gap-5 p-5 sm:p-6">
            <h3 class="nadia-chat-bold text-center break-words text-ink">{{ title }}</h3>
            <ChatCollaborationSummary :card="card" />
            <BaseDrawer v-model:open="isDetailsOpen" :title="title" title-voice="serif" mobile-layout="fullscreen" :reserve-mobile-nav-space="false">
                <template #trigger>
                    <BaseButton theme="neutral" size="sm" class="w-full">{{ t("chat.collaboration.seeMore") }}</BaseButton>
                </template>
                <div class="p-5 sm:p-6">
                    <ChatCollaborationSummary :card="card" />
                </div>
                <div v-if="visibleInsights.length" class="flex flex-col gap-6 border-t border-border p-5 sm:p-6">
                    <h3 class="body-semibold text-ink">{{ t("chat.collaboration.insightsTitle") }}</h3>
                    <section
                        v-for="(insight, index) in visibleInsights"
                        :key="index"
                        class="flex flex-col gap-4 border-t border-border pt-6 first-of-type:border-0 first-of-type:pt-0"
                        :aria-label="insight.score?.title ?? insight.title">
                        <div class="flex flex-col gap-2">
                            <h4 class="body-semibold flex items-center gap-2 text-ink">
                                <img v-if="insight.score && dimensionIcons[insight.dimensionId]" :src="dimensionIcons[insight.dimensionId]" alt="" class="size-5 shrink-0" />
                                {{ insight.score?.title ?? insight.title }}
                            </h4>
                            <p class="body-small text-ink">{{ insight.interpretation }}</p>
                        </div>
                        <PairScoreScale
                            v-if="insight.score"
                            :dimension="insight.dimensionId"
                            :viewer="insight.score.viewer"
                            :colleague="insight.score.colleague"
                            :colleague-name="card.person_name ?? t('chat.collaboration.colleague')"
                            :left-label="insight.score.left_label"
                            :right-label="insight.score.right_label"
                            compact />
                    </section>
                    <p v-if="hasScores" class="scaffold-details-small text-ink-muted">{{ t("chat.collaboration.scoreContext") }}</p>
                </div>
                <BaseAccordion
                    v-if="sourceLabels.length || card.limitations?.length"
                    :accordion-ids="['collaboration-sources']"
                    class="border-t border-border text-ink-muted"
                    item-class="rounded-none"
                    trigger-class="px-5 py-3 sm:px-6 rounded-none text-left focus-ring"
                    content-class="motion-reduce:animate-none">
                    <template #trigger-collaboration-sources>
                        <span class="scaffold-body-small flex items-center gap-2"
                            ><i class="bi bi-journal-text" aria-hidden="true"></i>{{ t("chat.collaboration.sourcesTitle") }}</span
                        >
                    </template>
                    <template #content-collaboration-sources>
                        <div class="flex flex-col gap-4 px-5 pb-5 sm:px-6 sm:pb-6">
                            <ul v-if="sourceLabels.length" :aria-label="t('chat.collaboration.sourcesTitle')" class="body-small flex flex-col gap-2">
                                <li v-for="source in sourceLabels" :key="source">{{ source }}</li>
                            </ul>
                            <div v-if="card.limitations?.length" class="flex flex-col gap-2 border-t border-border pt-4">
                                <h4 class="body-small-semibold text-ink">{{ t("chat.collaboration.limitationsTitle") }}</h4>
                                <ul class="body-small flex flex-col gap-2">
                                    <li v-for="limitation in card.limitations" :key="limitation">{{ limitation }}</li>
                                </ul>
                            </div>
                        </div>
                    </template>
                </BaseAccordion>
            </BaseDrawer>
        </div>
        <div v-else class="flex items-center gap-3 p-4">
            <BaseAvatar shape="square" size="md" aria-hidden="true">
                <i class="bi bi-x-circle-fill"></i>
            </BaseAvatar>
            <div class="flex min-w-0 flex-col gap-0">
                <h3 class="nadia-chat-bold text-ink">{{ heading }}</h3>
                <p v-if="card?.person_name" class="body-small text-ink-muted">{{ title }}</p>
                <p v-if="statusMessage" class="scaffold-details-small text-ink-muted">{{ statusMessage }}</p>
            </div>
        </div>
        <p class="sr-only" data-testid="collaboration-status">{{ statusMessage }}</p>
    </BaseCard>
</template>

<script setup lang="ts">
import decisionMakingIcon from "~img/perspective/decision-making.svg";
import energyIcon from "~img/perspective/energy.svg";
import infoProcessingIcon from "~img/perspective/information-processing.svg";
import organizationIcon from "~img/perspective/organization.svg";
import ChatCollaborationSummary from "~vue/ChatDetail/ChatCollaborationSummary.vue";
import { collaborationCard, collaborationStatusMessage } from "~vue/ChatDetail/collaborationStatus";
import BaseAccordion from "~vue/components/BaseAccordion.vue";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDrawer from "~vue/components/BaseDrawer.vue";
import PairScoreScale from "~vue/Connections/PairScoreScale.vue";
import type { ChatMessageLine } from "~vue/types/chat";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const dimensionIcons: Readonly<Record<string, string>> = { EI: energyIcon, SN: infoProcessingIcon, TF: decisionMakingIcon, JP: organizationIcon };

defineOptions({ inheritAttrs: false });
const { t } = useI18n();
const isDetailsOpen = ref(false);
const { completed, action = null } = defineProps<{
    completed: boolean;
    action?: ChatMessageLine | null;
}>();
const card = computed(() => collaborationCard(action));
const visibleInsights = computed(() =>
    (card.value?.insights?.slice(0, 3) ?? []).map((insight) => ({
        ...insight,
        score: insight.sublens ?? insight.dimension,
        dimensionId: insight.sublens?.dimension_id ?? insight.dimension?.id ?? "",
    })),
);
const hasScores = computed(() => visibleInsights.value.some((insight) => insight.score));
const sourceLabels = computed(() => [...new Set(card.value?.sources_used ?? [])]);
const title = computed(() => (card.value?.person_name ? t("chat.collaboration.pairTitle", { name: card.value.person_name }) : t("chat.collaboration.fallbackTitle")));
const heading = computed(() => {
    if (card.value?.status === "complete") {
        return t("chat.collaboration.completeHeading");
    }
    if (card.value?.status === "removed") {
        return t("chat.collaboration.unavailableHeading");
    }
    return t("chat.collaboration.fallbackHeading");
});
const statusMessage = computed(() => collaborationStatusMessage(card.value, completed, t));
</script>
